// Use production API URL by default, fallback to localhost only for local development
const getApiBaseUrl = () => {
  // If environment variable is set, use it (allows Cloudflare Pages to override)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // If running on localhost, use local backend
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5002';
  }
  
  // Otherwise, use production API URL (for soleco.pages.dev)
  return 'https://insight-api-ai.azurewebsites.net';
};

const API_BASE_URL = getApiBaseUrl();

export interface UserInfo {
  username: string;
  email: string;
  sites: string[];
  role: string;
}

export interface ChatRequest {
  message: string;
  conversationId?: string;
  stream?: boolean;
}

export interface ChatResponse {
  conversationId: string;
  message: string;
  data?: Record<string, number | null>;
  chartData?: {
    chartType: 'line' | 'bar' | 'area';
    title: string;
    series: Array<{
      name: string;
      data: number[];
      unit: string;
      color: string;
    }>;
    timeLabels: string[];
  };
  chartHint?: {
    suggested: boolean;
    suggestedTags: string[];
    suggestedTimeRange?: string;
    chartType?: 'line' | 'bar' | 'area';
    suggestedStartDate?: string;
    suggestedEndDate?: string;
  };
  timestamp: string;
  processingTime?: number;
}

class ApiService {
  private baseUrl: string;
  private credentials: string | null = null;
  // Timeout settings: longer for mobile networks, shorter for desktop
  private readonly CHAT_TIMEOUT_MS = 180000; // 3 minutes for chat (complex queries can take time)
  private readonly DEFAULT_TIMEOUT_MS = 30000; // 30 seconds for other requests

  constructor() {
    // Remove trailing slash to prevent double slashes in URLs
    this.baseUrl = API_BASE_URL.replace(/\/+$/, '');
    // Load credentials from localStorage on initialization
    const stored = localStorage.getItem('auth_credentials');
    if (stored) {
      this.credentials = stored;
    }
  }

  /**
   * Wraps fetch with timeout support using AbortController
   * @param url - Request URL
   * @param options - Fetch options
   * @param timeoutMs - Timeout in milliseconds
   * @returns Promise that rejects with timeout error if timeout is exceeded
   */
  private async fetchWithTimeout(
    url: string,
    options: RequestInit = {},
    timeoutMs: number = this.DEFAULT_TIMEOUT_MS
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try a simpler question or check your connection.');
      }
      throw error;
    }
  }

  setCredentials(username: string, password: string) {
    // Create Basic Auth credentials
    const encoded = btoa(`${username}:${password}`);
    this.credentials = encoded;
    // Store in localStorage
    localStorage.setItem('auth_credentials', encoded);
  }

  clearCredentials() {
    this.credentials = null;
    localStorage.removeItem('auth_credentials');
    localStorage.removeItem('selected_site');
    localStorage.removeItem('user_info');
  }

  getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.credentials) {
      headers['Authorization'] = `Basic ${this.credentials}`;
    }

    return headers;
  }

  async getUserInfo(): Promise<UserInfo> {
    const response = await this.fetchWithTimeout(
      `${this.baseUrl}/api/chat/user/info`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      },
      this.DEFAULT_TIMEOUT_MS
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      throw new Error(`Failed to get user info: ${response.statusText}`);
    }

    const data = await response.json();
    // Store user info in localStorage
    localStorage.setItem('user_info', JSON.stringify(data));
    return data;
  }

  async sendChatMessage(siteId: string, message: string, conversationId?: string): Promise<ChatResponse> {
    const response = await this.fetchWithTimeout(
      `${this.baseUrl}/api/chat/${encodeURIComponent(siteId)}/query`,
      {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          message,
          conversationId,
          stream: false,
        }),
      },
      this.CHAT_TIMEOUT_MS // Use longer timeout for chat requests
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      if (response.status === 403) {
        throw new Error('Access denied to this site');
      }
      if (response.status === 404) {
        // Handle "No data available" error from backend
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(errorData.error || 'No data available for this site');
      }
      if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please wait before trying again.');
      }
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to send message: ${response.statusText}`);
    }

    return response.json();
  }

  async getConversations(siteId: string): Promise<string[]> {
    const response = await this.fetchWithTimeout(
      `${this.baseUrl}/api/chat/${encodeURIComponent(siteId)}/conversations`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      },
      this.DEFAULT_TIMEOUT_MS
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      if (response.status === 403) {
        throw new Error('Access denied to this site');
      }
      throw new Error(`Failed to get conversations: ${response.statusText}`);
    }

    return response.json();
  }

  async getConversationHistory(siteId: string, conversationId: string): Promise<any[]> {
    const response = await this.fetchWithTimeout(
      `${this.baseUrl}/api/chat/${encodeURIComponent(siteId)}/conversations/${encodeURIComponent(conversationId)}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      },
      this.DEFAULT_TIMEOUT_MS
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      if (response.status === 403) {
        throw new Error('Access denied to this site');
      }
      throw new Error(`Failed to get conversation history: ${response.statusText}`);
    }

    return response.json();
  }

  async getDashboardData(siteId: string, hours: number = 720, aggregate: boolean = true): Promise<DashboardDataResponse> {
    // Default to a bounded range with aggregation for stable dashboard performance
    const response = await this.fetchWithTimeout(
      `${this.baseUrl}/api/chat/${encodeURIComponent(siteId)}/dashboard/data?hours=${hours}&aggregate=${aggregate}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      },
      this.DEFAULT_TIMEOUT_MS
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Unauthorized');
      }
      if (response.status === 403) {
        throw new Error('Access denied to this site');
      }
      throw new Error(`Failed to get dashboard data: ${response.statusText}`);
    }

    return response.json();
  }
}

export interface DashboardDataResponse {
  sensorData: SensorDataGroup[];
}

export interface SensorDataGroup {
  tagId: string;
  label?: string;
  unit?: string;
  status?: string;
  samples: SensorDataPoint[];
}

export interface SensorDataPoint {
  utc: number;
  value?: number;
  status: number;
}

export const apiService = new ApiService();

